import { coverCrop, wrapText, wrapTextWhole, type WallpaperBlock, type WallpaperLayout } from '@/lib/scheduleWallpaper'
import { getGradientPair } from '@/lib/utils'
import { setImages } from '@/lib/setImages'
import { setLabel } from '@/lib/setLabel'
import type { Artist, PartialRange } from '@/types'

// The lock-screen image, drawn straight onto a canvas: the schedule's cards
// (photo framed at its focal point, dark overlay, status border, faded and
// grayscale outside the highlight), stage headers, hour axis, header and
// footer. It used to be the DOM rendered to an image (modern-screenshot), but
// rasterizing that SVG took 15–40s on the main thread in desktop Chrome and on
// iPhones, and WebKit framed the photos wrong. Drawing it is milliseconds.
// Everything is in the layout's logical px; `pixelScale` maps them to pixels.

const FONT = '"Geist Variable", sans-serif'
const LINE = 1.2

export interface WallpaperTheme {
  background: string
  foreground: string
  mutedForeground: string
  muted: string
  border: string
  primary: string
  going: string
  interested: string
  onImage: string
}

const TOKENS: Record<keyof WallpaperTheme, string> = {
  background: '--background',
  foreground: '--foreground',
  mutedForeground: '--muted-foreground',
  muted: '--muted',
  border: '--border',
  primary: '--primary',
  going: '--going',
  interested: '--interested',
  onImage: '--on-image',
}

/** The theme tokens as colors a canvas takes, read off the live document. */
export function readWallpaperTheme(): WallpaperTheme {
  const probe = document.createElement('span')
  document.body.appendChild(probe)
  const theme = {} as WallpaperTheme
  for (const [key, token] of Object.entries(TOKENS) as [keyof WallpaperTheme, string][]) {
    probe.style.color = `var(${token})`
    theme[key] = getComputedStyle(probe).color
  }
  probe.remove()
  return theme
}

export interface WallpaperText {
  eventName: string
  dateLabel: string
  appName: string
  footer: string
  /** Why the image has no sets: every stage hidden, or none in the picked hours. */
  emptyLabel: Record<NonNullable<WallpaperLayout['empty']>, string>
  hourLabel: (minutes: number) => string
  clock: (hhmm: string) => string
  clockRange: (start: string, end: string) => string
}

export interface WallpaperDrawing {
  layout: WallpaperLayout
  artists: Record<string, Artist>
  text: WallpaperText
  theme: WallpaperTheme
  /** Photos by URL; a missing one draws the card's seed gradient instead. */
  images: ReadonlyMap<string, ImageBitmap>
  logo: CanvasImageSource | null
  /** The viewer's own partial ranges, by set: the slice they'll catch. */
  ranges: ReadonlyMap<string, PartialRange>
}

export function drawWallpaper(ctx: CanvasRenderingContext2D, d: WallpaperDrawing, pixelScale: number) {
  const { frame } = d.layout
  ctx.save()
  ctx.setTransform(pixelScale, 0, 0, pixelScale, 0, 0)
  ctx.fillStyle = d.theme.background
  ctx.fillRect(0, 0, frame.width, frame.height)
  drawHeader(ctx, d)
  if (d.layout.empty) {
    font(ctx, 400, 14 * frame.zoom)
    ctx.fillStyle = d.theme.mutedForeground
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    ctx.fillText(d.text.emptyLabel[d.layout.empty], frame.width / 2, frame.gridTop + 120 * frame.zoom)
  } else {
    drawGrid(ctx, d)
    for (const block of d.layout.blocks) drawCard(ctx, d, block)
  }
  drawFooter(ctx, d)
  ctx.restore()
}

/** The full-size PNG: the same drawing as the preview, at the export's pixel density. */
export function exportWallpaper(d: WallpaperDrawing): Promise<Blob> {
  const { frame } = d.layout
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(frame.width * frame.scale)
  canvas.height = Math.round(frame.height * frame.scale)
  const ctx = canvas.getContext('2d')
  if (!ctx) return Promise.reject(new Error('no 2d canvas context'))
  drawWallpaper(ctx, d, frame.scale)
  return new Promise((resolve, reject) =>
    canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null'))), 'image/png'),
  )
}

function font(ctx: CanvasRenderingContext2D, weight: number, size: number) {
  ctx.font = `${weight} ${size}px ${FONT}`
}

function ellipsize(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  return wrapText(s => ctx.measureText(s).width, text.replace(/\s+/g, ' '), maxWidth, 1)[0] ?? ''
}

function drawHeader(ctx: CanvasRenderingContext2D, { layout: { frame }, text, theme }: WallpaperDrawing) {
  const z = frame.zoom
  const bottom = frame.gridTop - 40 * z
  ctx.textAlign = 'center'
  ctx.textBaseline = 'bottom'
  font(ctx, 400, 12 * z)
  ctx.fillStyle = theme.mutedForeground
  ctx.fillText(text.dateLabel, frame.width / 2, bottom)
  font(ctx, 600, 15 * z)
  ctx.fillStyle = theme.foreground
  ctx.fillText(ellipsize(ctx, text.eventName, frame.width - 48 * z), frame.width / 2, bottom - 12 * z * 1.3 - 2 * z)
}

function drawGrid(ctx: CanvasRenderingContext2D, { layout, text, theme }: WallpaperDrawing) {
  const { frame } = layout
  const z = frame.zoom
  for (const c of layout.columns) {
    const top = frame.gridTop - 36 * z
    ctx.beginPath()
    ctx.roundRect(c.left, top, c.width, 24 * z, 6 * z)
    ctx.fillStyle = theme.muted
    ctx.fill()
    ctx.lineWidth = 1
    ctx.strokeStyle = theme.border
    ctx.stroke()
    font(ctx, 600, Math.min(12 * z, c.width / 8))
    ctx.fillStyle = theme.foreground
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(ellipsize(ctx, c.stage.name, c.width - 8), c.left + c.width / 2, top + 12 * z)
  }
  for (const h of layout.hours) {
    ctx.fillStyle = theme.border
    ctx.fillRect(frame.gutter + frame.axis, h.top, frame.width - 2 * frame.gutter - frame.axis, 1)
    font(ctx, 500, 12 * z)
    ctx.fillStyle = theme.mutedForeground
    ctx.textAlign = 'right'
    ctx.textBaseline = 'middle'
    ctx.fillText(text.hourLabel(h.minutes), frame.gutter + frame.axis - 4 * z, h.top)
  }
}

type Box = { x: number; y: number; w: number; h: number }

function drawCard(ctx: CanvasRenderingContext2D, d: WallpaperDrawing, block: WallpaperBlock) {
  const { status, dimmed } = block
  const box = { x: block.left, y: block.top + 2, w: block.width, h: Math.max(block.height - 4, 4) }
  const radius = 4
  const picked = status !== null
  const slice = picked ? partialSlice(block, d.ranges.get(block.set.id), box) : null

  ctx.save()
  roundRect(ctx, box, radius)
  ctx.clip()
  if (slice) {
    // A partial range: the whole set grayscale and faded, the slice the viewer
    // will catch in color — the schedule's activeWindow (CardBackdrop).
    drawBackdrop(ctx, d, block, box, box, { grayscale: true, overlay: 0.6 })
    ctx.globalAlpha = 0.4
    ctx.fillStyle = d.theme.background
    ctx.fillRect(box.x, box.y, box.w, box.h)
    ctx.globalAlpha = 1
    ctx.save()
    roundRect(ctx, slice, radius)
    ctx.clip()
    drawBackdrop(ctx, d, block, box, box, { grayscale: false, overlay: null })
    ctx.restore()
  } else {
    drawBackdrop(ctx, d, block, box, box, { grayscale: dimmed, overlay: null })
  }
  ctx.restore()

  if (slice) {
    stroke(ctx, box, radius, 1, d.theme.border, 1)
    stroke(ctx, slice, radius, 2, status === 'going' ? d.theme.going : d.theme.interested, 1)
  } else {
    const color = status === 'going' ? d.theme.going : status === 'interested' ? d.theme.interested : d.theme.primary
    stroke(ctx, box, radius, picked ? 2 : 1, color, picked ? 1 : 0.2)
  }

  // Text above the borders. A partial range's label goes in its slice, the part
  // of the card that's lit, when the slice has room for a line.
  const k = d.layout.frame.cardScale
  const textBox = slice && slice.h >= 14 * k ? slice : box
  ctx.save()
  roundRect(ctx, box, radius)
  ctx.clip()
  drawCardText(ctx, d, block, textBox)
  ctx.restore()

  if (dimmed) {
    // The schedule's 30% opacity, as the solid page background laid over at 70%.
    roundRect(ctx, box, radius)
    ctx.globalAlpha = 0.7
    ctx.fillStyle = d.theme.background
    ctx.fill()
    ctx.globalAlpha = 1
  }
}

function roundRect(ctx: CanvasRenderingContext2D, { x, y, w, h }: Box, radius: number) {
  ctx.beginPath()
  ctx.roundRect(x, y, w, h, radius)
}

function stroke(ctx: CanvasRenderingContext2D, box: Box, radius: number, width: number, color: string, alpha: number) {
  const inset = width / 2
  roundRect(ctx, { x: box.x + inset, y: box.y + inset, w: box.w - width, h: box.h - width }, radius)
  ctx.lineWidth = width
  ctx.strokeStyle = color
  ctx.globalAlpha = alpha
  ctx.stroke()
  ctx.globalAlpha = 1
}

// The viewer's own slice of a set as a box inside the card, or null when they
// catch all of what the image shows (no range, or a range covering it). The
// range is minutes from the set's start; the card may show only part of the
// set (cut to the picked hours).
function partialSlice(block: WallpaperBlock, range: PartialRange | undefined, box: Box): Box | null {
  if (!range) return null
  const [setStart, setEnd] = block.setSpan
  const [from, to] = block.span
  const start = Math.max(from, setStart + range.startMin)
  const end = Math.min(to, setStart + range.endMin)
  if (end <= start || (start <= from && end >= to) || setEnd <= setStart) return null
  const pxPerMin = box.h / (to - from)
  return { x: box.x, y: box.y + (start - from) * pxPerMin, w: box.w, h: (end - start) * pxPerMin }
}

// The card's picture: the artist photo framed at its focal point (or the seed
// gradient), optionally grayscale, under its dark overlay. `frame` is the box
// the picture is laid out in; drawing stays inside whatever clip is active, so
// a partial range's slice redraws it aligned with the full card.
function drawBackdrop(
  ctx: CanvasRenderingContext2D,
  d: WallpaperDrawing,
  { set, status }: WallpaperBlock,
  frame: Box,
  area: Box,
  { grayscale, overlay }: { grayscale: boolean; overlay: number | null },
) {
  const picked = status !== null
  const images = setImages(set, d.artists)
  const photo = images.imageUrl ? d.images.get(images.imageUrl) : undefined
  if (photo) {
    const focal = images.focal
    const { sx, sy, sw, sh } = coverCrop(photo, { width: frame.w, height: frame.h }, {
      x: focal?.imageFocalX ?? 50,
      y: focal?.imageFocalY ?? 0,
    })
    ctx.drawImage(photo, sx, sy, sw, sh, frame.x, frame.y, frame.w, frame.h)
  } else {
    const [c1, c2] = getGradientPair(images.seed)
    const gradient = ctx.createLinearGradient(frame.x, frame.y, frame.x + frame.w, frame.y + frame.h)
    gradient.addColorStop(0, c1)
    gradient.addColorStop(1, c2)
    ctx.fillStyle = gradient
    ctx.fillRect(area.x, area.y, area.w, area.h)
  }
  if (grayscale) {
    // The schedule's grayscale filter, as a blend: a zero-saturation fill takes
    // the color out of whatever is under it.
    ctx.globalCompositeOperation = 'saturation'
    ctx.fillStyle = '#808080'
    ctx.fillRect(area.x, area.y, area.w, area.h)
    ctx.globalCompositeOperation = 'source-over'
  }
  // The card's dark overlay, lighter on your own picks (InterestCard's imgOverlay / noImgOverlay),
  // plus a fade from the top so the name and time read over a bright photo.
  const alpha = overlay ?? (photo ? (picked ? 0.5 : 0.75) : picked ? 0.65 : 0.85)
  ctx.fillStyle = `rgba(0,0,0,${alpha})`
  ctx.fillRect(area.x, area.y, area.w, area.h)
  if (photo) {
    const fade = ctx.createLinearGradient(0, frame.y, 0, frame.y + Math.min(frame.h, 90))
    fade.addColorStop(0, 'rgba(0,0,0,0.45)')
    fade.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.fillStyle = fade
    ctx.fillRect(area.x, area.y, area.w, area.h)
  }
}

// The schedule card's type sizes (cardNameSize's steps for the box the card
// would have on the schedule, times cardScale: a wallpaper can't be zoomed)
// are the ceiling, stopping at the medium step and at MAX_NAME_PT as it lands
// on the phone — a day with few stages isn't zoomed out, so its cards would
// otherwise get giant type. On a crowded day a short card often can't fit its name whole at that
// size, or leaves no room for the time; then the text drops a step at a time
// (TEXT_STEPS, so neighbouring cards land on the same few sizes) to the
// largest that fits the name whole and the time under it, else the name alone.
// A name that won't fit even then is cut with "…". A card too short for a line
// at the smallest step (a 15-minute set on a long day) gets one line sized to
// fit inside its border, centered — or no text at all once that would be
// smaller than MIN_LEGIBLE_PT on the phone; its border and place on the grid
// still say what it is. The time is the start time only (the
// card's length already shows when the set ends) and stays near its own size —
// it doesn't follow the name down past MIN_TIME_SCALE. The label sits at the
// top with the schedule card's padding; only when that doesn't leave room for
// it do the top and bottom padding shrink — evenly, so a squeezed label never
// hugs the top over an empty bottom.
const TEXT_STEPS = [1, 0.86, 0.72]
const MAX_NAME_PT = 12
const MAX_TIME_PT = 10.5
const MIN_LEGIBLE_PT = 6.5
const MIN_TIME_SCALE = 0.86

function drawCardText(
  ctx: CanvasRenderingContext2D,
  { artists, text, theme, layout }: WallpaperDrawing,
  { set, status }: WallpaperBlock,
  box: Box,
) {
  const k = layout.frame.cardScale
  const boxW = box.w / k
  const z = layout.frame.zoom
  const nameMax = Math.min(k * (boxW < 88 ? 10 : boxW < 128 ? 12 : 14), MAX_NAME_PT * z)
  const timeMax = Math.min(k * (boxW < 128 ? 10 : 12), MAX_TIME_PT * z)
  const padX = 4 * k
  const padY = (box.h / k < 30 ? 1 : 2.5) * k
  const innerW = box.w - 2 * padX
  const innerH = box.h - 2 * padY
  const name = setLabel(set, artists).name
  const time = text.clock(set.startTime)

  type Fit = { size: number; lines: string[]; timeSize?: number }
  let fit: Fit | null = null
  for (const step of TEXT_STEPS) {
    const size = nameMax * step
    font(ctx, 600, size)
    const lines = wrapTextWhole(s => ctx.measureText(s).width, name, innerW, Math.floor(innerH / (size * LINE)))
    if (!lines) continue
    const timeSize = Math.min(timeMax, Math.max(size * 0.85, timeMax * MIN_TIME_SCALE))
    font(ctx, 400, timeSize)
    const timeFits = ctx.measureText(time).width <= innerW && innerH - lines.length * size * LINE >= timeSize * LINE
    if (timeFits) {
      fit = { size, lines, timeSize }
      break
    }
    // The name alone at this step beats shrinking two more steps for the time.
    fit ??= { size, lines }
  }

  let size: number
  let lines: string[]
  const smallest = nameMax * TEXT_STEPS[TEXT_STEPS.length - 1]
  if (fit) {
    ;({ size, lines } = fit)
  } else if (innerH >= smallest * LINE) {
    size = smallest
    font(ctx, 600, size)
    lines = wrapText(s => ctx.measureText(s).width, name, innerW, Math.floor(innerH / (size * LINE)))
  } else {
    // Glyphs (cap height plus descenders) take about the font size; keep them inside the border.
    const border = status !== null ? 2 : 1
    size = Math.min(smallest, box.h - 2 * border - 1)
    if (size < MIN_LEGIBLE_PT * z) return
    font(ctx, 600, size)
    ctx.fillStyle = theme.onImage
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    ctx.fillText(wrapText(s => ctx.measureText(s).width, name, innerW, 1)[0] ?? '', box.x + padX, box.y + box.h / 2 + size * 0.04)
    return
  }

  const blockH = lines.length * size * LINE + (fit?.timeSize ? fit.timeSize * LINE + 0.5 * k : 0)
  const roomyPad = (box.h / k >= 85 ? 5 : 3) * k
  let top = box.y + Math.min(roomyPad, (box.h - blockH) / 2)

  font(ctx, 600, size)
  ctx.fillStyle = theme.onImage
  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'
  top += (size * (LINE - 1)) / 2
  for (const line of lines) {
    ctx.fillText(line, box.x + padX, top)
    top += size * LINE
  }
  if (fit?.timeSize) {
    font(ctx, 400, fit.timeSize)
    ctx.globalAlpha = 0.75
    ctx.fillText(time, box.x + padX, top + 0.5 * k)
    ctx.globalAlpha = 1
  }
}

function drawFooter(ctx: CanvasRenderingContext2D, { layout: { frame }, text, theme, logo }: WallpaperDrawing) {
  const z = frame.zoom
  const cx = frame.width / 2
  const footerBottom = frame.height - 24 * z
  ctx.textAlign = 'center'
  ctx.textBaseline = 'bottom'
  font(ctx, 500, 11 * z)
  ctx.fillStyle = theme.mutedForeground
  ctx.fillText(text.footer, cx, footerBottom)

  const rowCenter = footerBottom - 11 * z * LINE - 2 * z - 9 * z
  font(ctx, 600, 13 * z)
  const logoSize = 18 * z
  const gap = 5 * z
  const left = cx - (logoSize + gap + ctx.measureText(text.appName).width) / 2
  if (logo) ctx.drawImage(logo, left, rowCenter - logoSize / 2, logoSize, logoSize)
  ctx.fillStyle = theme.foreground
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText(text.appName, left + logoSize + gap, rowCenter)
}
